import React, { Component } from 'react';
import {
  Button,
  Dropdown,
  Divider,
  Form,
  Grid,
  TextArea,
  Loader
} from 'semantic-ui-react';
import _ from 'lodash';

import {
  generateWebsiteAudience,
  generateVideoAudience,
  generateDomesticAudience,
  generateCanvasAudience
} from './AudienceCreator';
import { getPages } from '../../fbApi/Page';
import { getAccount } from '../../fbApi/Account';
import { getAccounts } from '../../services/AccountService';
import { audiences } from './Audiences';
import './AudienceBuilder.css';

class AudienceBuilder extends Component {
  state = {
    token: this.props.getFbToken(),
    audiences,
    showFields: false,
    accountOptions: null,
    processing: false,
    selectedAccount: '',
    selectedAudience: '',
    message: '',
    logText: '',
    pages: [],
    selectedPage: '',
    form: {
      pageId: '',
      pixel: ''
    },
    generatingAll: false
  };

  componentDidMount() {
    this.props.setSectionTitle('Audience Builder');
    this.props.saveGTMEvent('Audience Builder', 'Audience Builder');
  }

  onAccountSelect = async (event, data) => {
    const { token } = this.state;
    const account = _.find(data.options, (option) => {
      return option.value === data.value;
    });
    const accountId = account.value;
    const accountData = await getAccount(accountId, token);
    const form = { ...this.state.form };

    if (accountData && accountData.adspixels && accountData.adspixels.data[0]) {
      form.pixel = accountData.adspixels.data[0].id;
    } else {
      form.pixel = '';
    }

    this.setState({ ...this.state, selectedAccount: accountId, form });
  };

  getAdAccounts = async () => {
    try {
      const result = await getAccounts();

      const accountOptions = _.sortBy(
        _.map(result, (op, index) => ({
          key: index,
          value: op.account_id,
          text: op.name
        })),
        'text'
      );

      this.setState({
        ...this.state,
        accountOptions
      });
    } catch (error) {
      console.log(error);
    }
  };

  getPages = async () => {
    const token = this.props.getFbToken();
    const pages = await getPages(token);
    const filteredPages = pages[0].data.concat(pages[1].data).filter((page) => {
      return page.access_token;
    });

    const pagesOptions = _.sortBy(
      _.map(filteredPages, (page, index) => ({
        key: index,
        value: { pageId: page.id, access_token: page.access_token },
        text: page.name
      })),
      'text'
    );

    this.setState({ ...this.state, pages: pagesOptions });
  };

  selectPage = (event, data) => {
    const selectedPage = _.find(data.options, (opt) => {
      return opt.value === data.value;
    });

    const form = { ...this.state.form };

    if (selectedPage) {
      form.pageId = selectedPage.value.pageId;
    }

    this.setState({ ...this.state, form });
  };

  onAudienceSelect = (event, data) => {
    const pkg = _.find(data.options, (option) => {
      return option.value === data.value;
    });
    const audienceName = pkg.value;

    this.getAdAccounts();
    this.getPages();
    this.setState({ showFields: true, selectedAudience: audienceName });
  };

  handleFormChange = (event, { name, value }) => {
    this.setState({ form: { ...this.state.form, [name]: value } });
  };

  generateAudience = async () => {
    const { selectedAccount, form, selectedAudience } = this.state;
    if (!selectedAccount) {
      this.setState({ ...this.state, message: 'You must select an account' });
      return;
    }

    if (!form.pixel) {
      this.setState({ ...this.state, message: 'Pixel has not been setup yet' });
      return;
    }

    switch (selectedAudience) {
      case 'Website':
        await this.generateWebsite();
        break;
      case 'Cinemagraph':
      case 'Video':
        await this.generateVideo();
        break;
      case 'Canvas':
        await this.generateCanvas();
        break;
      case 'Domestic':
        await this.generateDomestic();
        break;
      case 'All':
        await this.generateAll();
        break;
      default:
        break;
    }
  };

  generateAll = async () => {
    this.setState({
      ...this.state,
      generatingAll: true,
      message: '',
      logText: ''
    });
    await this.generateWebsite();
    await this.generateVideo();
    await this.generateCanvas();
    this.setState({ ...this.state, generatingAll: false });
  };

  generateDomestic = async () => {
    const {
      generatingAll,
      logText: stateLogText,
      selectedAccount,
      token
    } = this.state;
    const logText = generatingAll ? stateLogText : '';
    this.setState({ ...this.state, processing: true, message: '', logText });

    const data = {
      accountId: selectedAccount,
      token
    };

    const resultDomesticAudience = await generateDomesticAudience(data);
    let finalLogText;

    if (resultDomesticAudience.id) {
      finalLogText = `${stateLogText}Domestic Audience - CREATED\n`;
    } else {
      finalLogText = `${stateLogText}Domestic Audience - ERROR:
      ${resultDomesticAudience.error.error_user_title}
      ${resultDomesticAudience.error.message}
      \n`;
    }

    this.setState({ ...this.state, processing: false, logText: finalLogText });
  };

  generateWebsite = async () => {
    const {
      generatingAll,
      logText: stateLogText,
      form,
      selectedAccount,
      token
    } = this.state;
    const logText = generatingAll ? stateLogText : '';
    this.setState({ ...this.state, processing: true, message: '', logText });

    const data = {
      pixel: form.pixel,
      accountId: selectedAccount,
      token
    };

    const resultWebsiteAudience = await generateWebsiteAudience(data);
    let finalLogText;

    if (resultWebsiteAudience.id) {
      finalLogText = `${stateLogText}Website Audience - CREATED\n`;
    } else {
      finalLogText = `${stateLogText}Website Audience - ERROR: 
      ${resultWebsiteAudience.error.error_user_title}
      ${resultWebsiteAudience.error.message}
      \n`;
    }

    this.setState({ ...this.state, processing: false, logText: finalLogText });
  };

  generateVideo = async () => {
    const {
      form,
      token,
      generatingAll,
      selectedAccount,
      logText: stateLogText
    } = this.state;
    const { pageId, pixel } = form;

    if (!pageId) {
      this.setState({ ...this.state, message: 'You must select a page' });
      return;
    }

    const logText = generatingAll ? stateLogText : '';
    this.setState({ ...this.state, processing: true, message: '', logText });

    const data = {
      pixel,
      accountId: selectedAccount,
      token,
      pageId
    };

    const resultVideosAudience = await generateVideoAudience(data);
    let finalLogText;

    if (resultVideosAudience.id) {
      finalLogText = `${stateLogText}Video Audience - CREATED\n`;
    } else {
      finalLogText = `${stateLogText} Video Audience - ERROR: 
      ${resultVideosAudience.error.error_user_title}
      ${resultVideosAudience.error.message}
      \n`;
    }

    this.setState({ ...this.state, processing: false, logText: finalLogText });
  };

  generateCanvas = async () => {
    const {
      form,
      token,
      generatingAll,
      selectedAccount,
      logText: stateLogText
    } = this.state;
    const { pageId, pixel } = form;

    if (!pageId) {
      this.setState({ ...this.state, message: 'You must select a page' });
      return;
    }

    const logText = generatingAll ? stateLogText : '';
    this.setState({ ...this.state, processing: true, message: '', logText });

    const data = {
      pixel,
      accountId: selectedAccount,
      token,
      pageId
    };

    const resultCanvasAudience = await generateCanvasAudience(data);
    let finalLogText;

    if (resultCanvasAudience.id) {
      finalLogText = `${stateLogText}Canvas Audience - CREATED\n`;
    } else {
      finalLogText = `${stateLogText}
      Canvas Audience - ERROR: 
      ${resultCanvasAudience.error.error_user_title}
      ${resultCanvasAudience.error.message}
      \n`;
    }

    this.setState({ ...this.state, processing: false, logText: finalLogText });
  };

  render() {
    const {
      showFields,
      form,
      accountOptions,
      pages,
      message,
      processing,
      logText,
      selectedAudience,
      audiences: stateAudiences
    } = this.state;
    return (
      <div className="form-container">
        <Grid>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Dropdown
                placeholder="Select Audience"
                search
                selection
                options={stateAudiences}
                onChange={this.onAudienceSelect}
              />
            </Grid.Column>
            <Grid.Column>
              {showFields ? (
                <div>
                  <div className="form-field">
                    <span className="label-name">Pixel:</span>
                    <Form.Input
                      className="input-form"
                      type="text"
                      name="pixel"
                      disabled
                      value={form.pixel}
                      onChange={this.handleFormChange}
                    />
                  </div>
                </div>
              ) : null}
            </Grid.Column>
            <Grid.Column>
              {showFields ? (
                <div>
                  <Dropdown
                    className="field-accounts"
                    placeholder="Ad Accounts"
                    search
                    selection
                    options={accountOptions}
                    onChange={this.onAccountSelect}
                  />
                  {selectedAudience !== 'Website' ? (
                    <Dropdown
                      className="field-accounts"
                      placeholder="Pages"
                      search
                      selection
                      options={pages}
                      onChange={this.selectPage}
                    />
                  ) : null}
                  <Button
                    className="floated left tiny grey"
                    onClick={() => this.generateAudience()}
                  >
                    Launch
                  </Button>
                </div>
              ) : null}
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider hidden />
        {message ? (
          <div>
            <span>{message}</span>
          </div>
        ) : null}
        {processing ? (
          <div className="processing-container">
            <span>
              {' '}
              <Loader inline className="active" /> Processing...{' '}
            </span>
          </div>
        ) : null}
        <Divider hidden />
        {showFields ? (
          <TextArea
            className="debug-log"
            placeholder="Debug Log"
            disabled
            rows="18"
            value={logText}
          />
        ) : null}
      </div>
    );
  }
}

export default AudienceBuilder;



// WEBPACK FOOTER //
// ./src/screens/AudienceBuilder/AudienceBuilder.js